import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Cell,
  PieChart,
  Pie,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Area,
  ReferenceLine,
  ComposedChart
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Calendar,
  Award,
  Flame,
  Zap,
  BarChart3,
  PieChart as PieChartIcon,
  ArrowLeft,
  Filter,
  Info,
  ChevronDown,
  ChevronUp,
  X,
  BookOpen,
  Target,
  Lightbulb,
  Layers,
  Eye,
  Play,
  ArrowUpRight,
  ArrowDownRight,
  BarChart2,
  Hash,
  Trophy,
  Medal
} from 'lucide-react';

// Paleta de colores para temas
const TOPIC_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#14B8A6', '#A855F7'
];

// Paleta de colores para subtemas (más suaves)
const SUBTOPIC_COLORS = [
  '#60A5FA', '#34D399', '#FBBF24', '#F87171', '#A78BFA',
  '#F472B6', '#22D3EE', '#A3E635', '#FB923C', '#818CF8',
  '#2DD4BF', '#C084FC'
];

// Extraer número de año de strings como "RM 2024"
const extractYear = (yearStr) => {
  if (!yearStr) return 0;
  const match = yearStr.match(/\d{4}/);
  return match ? parseInt(match[0], 10) : 0;
};

// Componente de Card de Estadística
const StatCard = ({ title, value, subtitle, icon: Icon, trend, darkMode, accentColor = 'blue', onClick, clickable = false }) => {
  const colorClasses = {
    blue: darkMode ? 'from-blue-500/20 to-cyan-500/20 border-blue-500/30' : 'from-blue-50 to-cyan-50 border-blue-200',
    green: darkMode ? 'from-emerald-500/20 to-green-500/20 border-emerald-500/30' : 'from-emerald-50 to-green-50 border-emerald-200',
    amber: darkMode ? 'from-amber-500/20 to-orange-500/20 border-amber-500/30' : 'from-amber-50 to-orange-50 border-amber-200',
    purple: darkMode ? 'from-purple-500/20 to-pink-500/20 border-purple-500/30' : 'from-purple-50 to-pink-50 border-purple-200',
    teal: darkMode ? 'from-teal-500/20 to-cyan-500/20 border-teal-500/30' : 'from-teal-50 to-cyan-50 border-teal-200',
  };

  const iconColors = {
    blue: darkMode ? 'text-blue-400' : 'text-blue-600',
    green: darkMode ? 'text-emerald-400' : 'text-emerald-600',
    amber: darkMode ? 'text-amber-400' : 'text-amber-600',
    purple: darkMode ? 'text-purple-400' : 'text-purple-600',
    teal: darkMode ? 'text-teal-400' : 'text-teal-600',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={clickable ? onClick : undefined}
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${colorClasses[accentColor]} p-5 ${
        darkMode ? 'bg-slate-900/60' : 'bg-white'
      } shadow-lg ${clickable ? 'cursor-pointer hover:scale-[1.02] transition-transform' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {title}
          </p>
          <p className={`mt-2 text-3xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
            {value}
          </p>
          {subtitle && (
            <p className={`mt-1 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              {subtitle}
            </p>
          )}
        </div>
        <div className={`rounded-xl p-3 ${darkMode ? 'bg-slate-800/50' : 'bg-white/80'} shadow-inner`}>
          <Icon className={`h-6 w-6 ${iconColors[accentColor]}`} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`mt-3 flex items-center gap-1 text-sm ${
          trend > 0 ? 'text-emerald-500' : trend < 0 ? 'text-rose-500' : darkMode ? 'text-slate-400' : 'text-slate-500'
        }`}>
          {trend > 0 ? <TrendingUp className="h-4 w-4" /> : trend < 0 ? <TrendingDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
          <span>{trend > 0 ? '+' : ''}{trend.toFixed(1)}% vs año anterior</span>
        </div>
      )}
      {clickable && (
        <div className={`absolute bottom-2 right-2 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          Click para detalles →
        </div>
      )}
    </motion.div>
  );
};

// Componente Mini Heatmap para Subtemas
const MiniSubtopicHeatmap = ({ data, years, darkMode, isExpanded = false, collapsedLimit = 10 }) => {
  const displayData = isExpanded ? data : data.slice(0, collapsedLimit);
  const maxValue = useMemo(() => {
    let max = 0;
    data.forEach(row => {
      years.forEach(year => {
        if (row[year] > max) max = row[year];
      });
    });
    return max || 1;
  }, [data, years]);

  const getHeatColor = (value) => {
    if (value === 0) return darkMode ? 'bg-slate-800/50' : 'bg-slate-100';
    const intensity = value / maxValue;
    if (intensity < 0.33) return darkMode ? 'bg-teal-900/60' : 'bg-teal-100';
    if (intensity < 0.66) return darkMode ? 'bg-teal-600/70' : 'bg-teal-400';
    return darkMode ? 'bg-teal-400' : 'bg-teal-600';
  };

  const getTextColor = (value) => {
    if (value === 0) return darkMode ? 'text-slate-600' : 'text-slate-400';
    const intensity = value / maxValue;
    if (intensity < 0.5) return darkMode ? 'text-teal-200' : 'text-teal-800';
    return 'text-white';
  };

  // Solo mostrar los últimos 10 años para el mini heatmap
  const displayYears = years.slice(-10);

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        {/* Header con años */}
        <div className="flex">
          <div className={`w-44 flex-shrink-0 p-1.5 text-xs font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Subtema
          </div>
          {displayYears.map(year => (
            <div
              key={year}
              className={`flex-1 p-1.5 text-center text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}
              style={{ minWidth: '40px' }}
            >
              {year.replace('RM ', "'")}
            </div>
          ))}
          <div className={`w-12 flex-shrink-0 p-1.5 text-center text-xs font-semibold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
            Total
          </div>
        </div>

        {/* Filas de subtemas */}
        {displayData.map((row, idx) => (
          <motion.div
            key={row.subtopic}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.02 }}
            className={`flex items-center border-t ${darkMode ? 'border-slate-700/30' : 'border-slate-200/50'}`}
          >
            <div
              className={`w-44 flex-shrink-0 p-1.5 text-xs font-medium truncate ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}
              title={row.subtopic}
            >
              {row.subtopic}
            </div>
            {displayYears.map(year => (
              <div
                key={`${row.subtopic}-${year}`}
                className="flex-1 p-0.5"
                style={{ minWidth: '40px' }}
              >
                <div
                  className={`mx-auto h-6 w-full max-w-[32px] rounded flex items-center justify-center text-xs font-semibold ${getHeatColor(row[year] || 0)} ${getTextColor(row[year] || 0)}`}
                  title={`${row.subtopic} - ${year}: ${row[year] || 0} preguntas`}
                >
                  {row[year] || '-'}
                </div>
              </div>
            ))}
            <div className={`w-12 flex-shrink-0 p-1.5 text-center text-xs font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
              {row.total}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Componente de Tabla de Probabilidad de Subtemas
const SubtopicProbabilityTable = ({ data, totalYears, darkMode, onSelectSubtopic }) => {
  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="h-3 w-3 text-emerald-500" />;
    if (trend === 'down') return <TrendingDown className="h-3 w-3 text-rose-500" />;
    return <Minus className="h-3 w-3 text-slate-400" />;
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 50) return darkMode ? 'text-emerald-400 bg-emerald-500/20' : 'text-emerald-700 bg-emerald-100';
    if (prob >= 30) return darkMode ? 'text-amber-400 bg-amber-500/20' : 'text-amber-700 bg-amber-100';
    if (prob >= 15) return darkMode ? 'text-blue-400 bg-blue-500/20' : 'text-blue-700 bg-blue-100';
    return darkMode ? 'text-slate-400 bg-slate-700/50' : 'text-slate-600 bg-slate-100';
  };

  const getPriorityBadge = (prob, trend) => {
    if (prob >= 40 || (prob >= 25 && trend === 'up')) {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
          darkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-100 text-rose-700'
        }`}>
          <Flame className="h-3 w-3" /> Prioridad Alta
        </span>
      );
    }
    if (prob >= 20) {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
          darkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
        }`}>
          Estudiar
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
        darkMode ? 'bg-slate-700/50 text-slate-400' : 'bg-slate-100 text-slate-500'
      }`}>
        Revisar
      </span>
    );
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/50 dark:border-slate-700/50">
      <div className="max-h-[400px] overflow-y-auto">
        <table className="w-full">
          <thead className={`sticky top-0 ${darkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
            <tr>
              <th className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Subtema
              </th>
              <th className={`px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Años
              </th>
              <th className={`px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Prob.
              </th>
              <th className={`px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Tend.
              </th>
              <th className={`px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Prioridad
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => (
              <motion.tr
                key={item.subtopic}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: idx * 0.03 }}
                onClick={() => onSelectSubtopic && onSelectSubtopic(item.subtopic)}
                className={`border-t cursor-pointer ${
                  darkMode
                    ? 'border-slate-700/30 hover:bg-slate-700/30'
                    : 'border-slate-200/50 hover:bg-slate-50'
                } transition-colors`}
              >
                <td className={`px-3 py-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  <span className="text-sm font-medium">{item.subtopic}</span>
                </td>
                <td className={`px-3 py-2 text-center text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {item.yearsAppeared}/{totalYears}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${getProbabilityColor(item.probability)}`}>
                    {item.probability}%
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-center">
                    {getTrendIcon(item.trend)}
                  </div>
                </td>
                <td className="px-3 py-2 text-center">
                  {getPriorityBadge(item.probability, item.trend)}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Componente Panel de Drill-down de Subtemas
const SubtopicDrilldownPanel = ({
  topic,
  questions,
  years,
  darkMode,
  onClose,
  onGenerateExam,
  onSelectSubtopic,
  isFullView = false
}) => {
  const [subtopicViewMode, setSubtopicViewMode] = useState('heatmap');
  const [isExpanded, setIsExpanded] = useState(isFullView); // Expandido por defecto en vista completa

  // Límites para visualización
  const COLLAPSED_LIMIT = 10;
  const CHART_LIMIT = 6;
  const RADAR_LIMIT = 8;

  // Filtrar preguntas del tema seleccionado
  const topicQuestions = useMemo(() => {
    return questions.filter(q => q.topic === topic);
  }, [questions, topic]);

  // Obtener subtemas únicos con conteos
  const subtopics = useMemo(() => {
    const subtopicCounts = {};
    topicQuestions.forEach(q => {
      const subtopic = q.subtopic || 'Sin especificar';
      subtopicCounts[subtopic] = (subtopicCounts[subtopic] || 0) + 1;
    });
    return Object.entries(subtopicCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([subtopic, count]) => ({ subtopic, count }));
  }, [topicQuestions]);

  // Datos para el heatmap de subtemas
  const subtopicHeatmapData = useMemo(() => {
    return subtopics.map(({ subtopic }) => {
      const row = { subtopic, total: 0 };
      years.forEach(year => {
        const count = topicQuestions.filter(q =>
          (q.subtopic || 'Sin especificar') === subtopic && q.year === year
        ).length;
        row[year] = count;
        row.total += count;
      });
      return row;
    });
  }, [subtopics, years, topicQuestions]);

  // Datos para tabla de probabilidad de subtemas
  const subtopicProbabilityData = useMemo(() => {
    return subtopics.map(({ subtopic, count }) => {
      const yearsWithSubtopic = new Set();
      topicQuestions.forEach(q => {
        if ((q.subtopic || 'Sin especificar') === subtopic && q.year) {
          yearsWithSubtopic.add(q.year);
        }
      });

      const yearsAppeared = yearsWithSubtopic.size;
      const probability = Math.round((yearsAppeared / years.length) * 100);

      // Calcular tendencia
      const recentYears = years.slice(-3);
      const previousYears = years.slice(-6, -3);

      const recentCount = topicQuestions.filter(q =>
        (q.subtopic || 'Sin especificar') === subtopic && recentYears.includes(q.year)
      ).length;

      const previousCount = topicQuestions.filter(q =>
        (q.subtopic || 'Sin especificar') === subtopic && previousYears.includes(q.year)
      ).length;

      let trend = 'stable';
      if (previousCount > 0) {
        const change = ((recentCount - previousCount) / previousCount) * 100;
        if (change > 30) trend = 'up';
        else if (change < -30) trend = 'down';
      } else if (recentCount > 0) {
        trend = 'up';
      }

      return {
        subtopic,
        total: count,
        yearsAppeared,
        probability,
        trend,
        recentCount
      };
    });
  }, [subtopics, years, topicQuestions]);

  // Datos para gráfico de barras (incluye todos los subtemas para cuando se expanda)
  const subtopicBarData = useMemo(() => {
    return years.slice(-8).map(year => {
      const yearData = { year: year.replace('RM ', '') };
      subtopics.forEach(({ subtopic }) => {
        yearData[subtopic] = topicQuestions.filter(q =>
          (q.subtopic || 'Sin especificar') === subtopic && q.year === year
        ).length;
      });
      return yearData;
    });
  }, [years, subtopics, topicQuestions]);

  // Datos para gráfico radar (incluye todos para cuando se expanda)
  const radarData = useMemo(() => {
    return subtopics.map(({ subtopic, count }) => ({
      subtopic: subtopic.length > 15 ? subtopic.substring(0, 15) + '...' : subtopic,
      fullName: subtopic,
      frecuencia: count,
      probabilidad: subtopicProbabilityData.find(s => s.subtopic === subtopic)?.probability || 0
    }));
  }, [subtopics, subtopicProbabilityData]);

  // Estadísticas del tema
  const topicStats = useMemo(() => {
    const totalQuestions = topicQuestions.length;
    const totalSubtopics = subtopics.length;
    const topSubtopic = subtopics[0] || { subtopic: 'N/A', count: 0 };
    const highPriorityCount = subtopicProbabilityData.filter(s =>
      s.probability >= 40 || (s.probability >= 25 && s.trend === 'up')
    ).length;

    return { totalQuestions, totalSubtopics, topSubtopic, highPriorityCount };
  }, [topicQuestions, subtopics, subtopicProbabilityData]);

  // Recomendaciones de estudio
  const studyRecommendations = useMemo(() => {
    const mustStudy = subtopicProbabilityData
      .filter(s => s.probability >= 40)
      .slice(0, 3);

    const trending = subtopicProbabilityData
      .filter(s => s.trend === 'up' && s.probability >= 15)
      .slice(0, 3);

    const recentHot = subtopicProbabilityData
      .filter(s => s.recentCount >= 2)
      .sort((a, b) => b.recentCount - a.recentCount)
      .slice(0, 3);

    return { mustStudy, trending, recentHot };
  }, [subtopicProbabilityData]);

  const WrapperComponent = isFullView ? 'div' : motion.div;
  const wrapperProps = isFullView ? {} : {
    initial: { opacity: 0, y: -20, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -20, scale: 0.98 },
    transition: { duration: 0.3, ease: 'easeOut' }
  };

  return (
    <WrapperComponent
      {...wrapperProps}
      className={`${isFullView ? '' : 'mt-4'} rounded-2xl border-2 ${
        darkMode
          ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-teal-500/30'
          : 'bg-gradient-to-br from-white to-slate-50 border-teal-300'
      } shadow-xl`}
    >
      {/* Header del Panel - solo mostrar si no es vista completa */}
      {!isFullView && (
        <div className={`px-6 py-4 border-b ${darkMode ? 'border-slate-700/50 bg-slate-800/50' : 'border-slate-200 bg-teal-50/50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl ${darkMode ? 'bg-teal-500/20' : 'bg-teal-100'}`}>
                <Layers className={`h-5 w-5 ${darkMode ? 'text-teal-400' : 'text-teal-600'}`} />
              </div>
              <div>
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                  Análisis de Subtemas: {topic}
                </h3>
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  {topicStats.totalQuestions} preguntas • {topicStats.totalSubtopics} subtemas
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-xl transition-colors ${
                darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-200 text-slate-600'
              }`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      <div className="p-6 space-y-6">
        {/* Stats Cards de Subtemas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-800/50' : 'bg-white'} border ${darkMode ? 'border-slate-700/50' : 'border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className={`h-4 w-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total Preguntas</span>
            </div>
            <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{topicStats.totalQuestions}</p>
          </div>

          <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-800/50' : 'bg-white'} border ${darkMode ? 'border-slate-700/50' : 'border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Layers className={`h-4 w-4 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Subtemas</span>
            </div>
            <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{topicStats.totalSubtopics}</p>
          </div>

          <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-800/50' : 'bg-white'} border ${darkMode ? 'border-slate-700/50' : 'border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Award className={`h-4 w-4 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
              <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Subtema Líder</span>
            </div>
            <p className={`text-sm font-bold truncate ${darkMode ? 'text-white' : 'text-slate-800'}`} title={topicStats.topSubtopic.subtopic}>
              {topicStats.topSubtopic.subtopic}
            </p>
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{topicStats.topSubtopic.count} preguntas</p>
          </div>

          <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-800/50' : 'bg-white'} border ${darkMode ? 'border-slate-700/50' : 'border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Flame className={`h-4 w-4 ${darkMode ? 'text-rose-400' : 'text-rose-600'}`} />
              <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Alta Prioridad</span>
            </div>
            <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{topicStats.highPriorityCount}</p>
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>subtemas clave</p>
          </div>
        </div>

        {/* Tabs de visualización */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'heatmap', label: 'Heatmap', icon: BarChart3 },
            { id: 'bars', label: 'Barras', icon: BarChart3 },
            { id: 'radar', label: 'Radar', icon: Target },
            { id: 'table', label: 'Tabla', icon: Eye },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSubtopicViewMode(id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                subtopicViewMode === id
                  ? darkMode
                    ? 'bg-teal-500 text-white'
                    : 'bg-teal-500 text-white'
                  : darkMode
                    ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Visualización de Subtemas */}
        <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/30' : 'bg-white'} border ${darkMode ? 'border-slate-700/30' : 'border-slate-200'}`}>
          {subtopicViewMode === 'heatmap' && (
            <MiniSubtopicHeatmap
              data={subtopicHeatmapData}
              years={years}
              darkMode={darkMode}
              isExpanded={isExpanded}
              collapsedLimit={COLLAPSED_LIMIT}
            />
          )}

          {subtopicViewMode === 'bars' && (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={subtopicBarData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                  <XAxis dataKey="year" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 11 }} />
                  <YAxis tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      color: darkMode ? '#f3f4f6' : '#1f2937',
                      fontSize: '12px'
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  {(isExpanded ? subtopics : subtopics.slice(0, CHART_LIMIT)).map(({ subtopic }, index) => (
                    <Bar
                      key={subtopic}
                      dataKey={subtopic}
                      stackId="a"
                      fill={SUBTOPIC_COLORS[index % SUBTOPIC_COLORS.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {subtopicViewMode === 'radar' && (
            <div className={isExpanded ? 'h-[450px]' : 'h-[300px]'}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={isExpanded ? radarData : radarData.slice(0, RADAR_LIMIT)}>
                  <PolarGrid stroke={darkMode ? '#374151' : '#e5e7eb'} />
                  <PolarAngleAxis
                    dataKey="subtopic"
                    tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                  />
                  <PolarRadiusAxis
                    angle={30}
                    domain={[0, 'auto']}
                    tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                  />
                  <Radar
                    name="Frecuencia"
                    dataKey="frecuencia"
                    stroke="#14b8a6"
                    fill="#14b8a6"
                    fillOpacity={0.5}
                  />
                  <Radar
                    name="Probabilidad %"
                    dataKey="probabilidad"
                    stroke="#f59e0b"
                    fill="#f59e0b"
                    fillOpacity={0.3}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}

          {subtopicViewMode === 'table' && (
            <SubtopicProbabilityTable
              data={subtopicProbabilityData}
              totalYears={years.length}
              darkMode={darkMode}
              onSelectSubtopic={onSelectSubtopic}
            />
          )}

          {/* Botón Expandir/Colapsar - solo mostrar si hay más subtemas que el límite */}
          {subtopics.length > COLLAPSED_LIMIT && subtopicViewMode !== 'table' && (
            <div className="mt-4 flex justify-center">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  darkMode
                    ? 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30 border border-teal-500/30'
                    : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200'
                }`}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Ver menos ({COLLAPSED_LIMIT} de {subtopics.length})
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Ver todos ({subtopics.length} subtemas)
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Recomendaciones de Estudio Dirigido */}
        <div className={`rounded-xl p-5 ${
          darkMode
            ? 'bg-gradient-to-br from-teal-900/30 to-cyan-900/30 border border-teal-500/30'
            : 'bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200'
        }`}>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className={`h-5 w-5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
            <h4 className={`font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Recomendaciones de Estudio Dirigido
            </h4>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Subtemas Imperdibles */}
            <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
              <h5 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-rose-400' : 'text-rose-700'}`}>
                <Flame className="h-4 w-4" />
                Imperdibles (40%+)
              </h5>
              {studyRecommendations.mustStudy.length > 0 ? (
                <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {studyRecommendations.mustStudy.map(s => (
                    <li key={s.subtopic} className="flex items-center justify-between">
                      <span className="truncate mr-2" title={s.subtopic}>{s.subtopic}</span>
                      <span className={`font-bold ${darkMode ? 'text-rose-400' : 'text-rose-600'}`}>{s.probability}%</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={`text-sm italic ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Ningún subtema supera el 40%
                </p>
              )}
            </div>

            {/* En Tendencia */}
            <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
              <h5 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                <TrendingUp className="h-4 w-4" />
                En Tendencia
              </h5>
              {studyRecommendations.trending.length > 0 ? (
                <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {studyRecommendations.trending.map(s => (
                    <li key={s.subtopic} className="flex items-center gap-2">
                      <TrendingUp className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                      <span className="truncate" title={s.subtopic}>{s.subtopic}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={`text-sm italic ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Sin tendencias claras
                </p>
              )}
            </div>

            {/* Recientes (últimos 3 años) */}
            <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
              <h5 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                <Zap className="h-4 w-4" />
                Frecuentes Recientes
              </h5>
              {studyRecommendations.recentHot.length > 0 ? (
                <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {studyRecommendations.recentHot.map(s => (
                    <li key={s.subtopic} className="flex items-center justify-between">
                      <span className="truncate mr-2" title={s.subtopic}>{s.subtopic}</span>
                      <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {s.recentCount} en 3 años
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={`text-sm italic ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Sin datos recientes suficientes
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Botón para Generar Examen */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => onGenerateExam && onGenerateExam(topic, null)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
              darkMode
                ? 'bg-teal-500 hover:bg-teal-400 text-white'
                : 'bg-teal-500 hover:bg-teal-600 text-white'
            } shadow-lg hover:shadow-xl`}
          >
            <Play className="h-4 w-4" />
            Practicar todo {topic}
          </button>

          {studyRecommendations.mustStudy.length > 0 && (
            <button
              onClick={() => onGenerateExam && onGenerateExam(topic, studyRecommendations.mustStudy.map(s => s.subtopic))}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                darkMode
                  ? 'bg-rose-500 hover:bg-rose-400 text-white'
                  : 'bg-rose-500 hover:bg-rose-600 text-white'
              } shadow-lg hover:shadow-xl`}
            >
              <Flame className="h-4 w-4" />
              Practicar subtemas clave
            </button>
          )}
        </div>
      </div>
    </WrapperComponent>
  );
};

// Componente Heatmap Principal
const TopicHeatmap = ({ data, years, darkMode, onTopicClick }) => {
  const maxValue = useMemo(() => {
    let max = 0;
    data.forEach(row => {
      years.forEach(year => {
        if (row[year] > max) max = row[year];
      });
    });
    return max || 1;
  }, [data, years]);

  const getHeatColor = (value) => {
    if (value === 0) return darkMode ? 'bg-slate-800' : 'bg-slate-100';
    const intensity = value / maxValue;
    if (intensity < 0.25) return darkMode ? 'bg-blue-900/50' : 'bg-blue-100';
    if (intensity < 0.5) return darkMode ? 'bg-blue-700/60' : 'bg-blue-300';
    if (intensity < 0.75) return darkMode ? 'bg-blue-500/70' : 'bg-blue-500';
    return darkMode ? 'bg-blue-400' : 'bg-blue-700';
  };

  const getTextColor = (value) => {
    if (value === 0) return darkMode ? 'text-slate-600' : 'text-slate-400';
    const intensity = value / maxValue;
    if (intensity < 0.5) return darkMode ? 'text-blue-200' : 'text-blue-800';
    return 'text-white';
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[800px]">
        {/* Header con años */}
        <div className="flex">
          <div className={`w-40 flex-shrink-0 p-2 text-xs font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Tema (doble click para ver)
          </div>
          {years.map(year => (
            <div
              key={year}
              className={`flex-1 p-2 text-center text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}
              style={{ minWidth: '50px' }}
            >
              {year.replace('RM ', "'")}
            </div>
          ))}
          <div className={`w-16 flex-shrink-0 p-2 text-center text-xs font-semibold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
            Total
          </div>
        </div>

        {/* Filas de temas */}
        {data.map((row, idx) => (
          <React.Fragment key={row.topic}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.03 }}
              className={`flex items-center border-t cursor-pointer transition-all ${
                darkMode ? 'border-slate-700/50' : 'border-slate-200'
              } ${darkMode ? 'hover:bg-slate-800/30' : 'hover:bg-slate-50'}`}
              onDoubleClick={() => onTopicClick && onTopicClick(row.topic)}
            >
              <div className={`w-40 flex-shrink-0 p-2 text-sm font-medium truncate flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`} title={`Doble click para ver subtemas de ${row.topic}`}>
                <Eye className={`h-4 w-4 flex-shrink-0 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                <span className="truncate">{row.topic}</span>
              </div>
              {years.map(year => (
                <div
                  key={`${row.topic}-${year}`}
                  className="flex-1 p-1"
                  style={{ minWidth: '50px' }}
                >
                  <div
                    className={`mx-auto h-8 w-full max-w-[40px] rounded-md flex items-center justify-center text-xs font-semibold transition-all hover:scale-110 ${getHeatColor(row[year])} ${getTextColor(row[year])}`}
                    title={`${row.topic} - ${year}: ${row[year]} preguntas`}
                  >
                    {row[year] || '-'}
                  </div>
                </div>
              ))}
              <div className={`w-16 flex-shrink-0 p-2 text-center text-sm font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                {row.total}
              </div>
            </motion.div>
          </React.Fragment>
        ))}
      </div>

      {/* Leyenda */}
      <div className={`mt-4 flex items-center justify-center gap-4 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
        <span>Intensidad:</span>
        <div className="flex items-center gap-1">
          <div className={`h-4 w-4 rounded ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`} />
          <span>0</span>
        </div>
        <div className="flex items-center gap-1">
          <div className={`h-4 w-4 rounded ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`} />
          <span>Bajo</span>
        </div>
        <div className="flex items-center gap-1">
          <div className={`h-4 w-4 rounded ${darkMode ? 'bg-blue-500/70' : 'bg-blue-500'}`} />
          <span>Medio</span>
        </div>
        <div className="flex items-center gap-1">
          <div className={`h-4 w-4 rounded ${darkMode ? 'bg-blue-400' : 'bg-blue-700'}`} />
          <span>Alto</span>
        </div>
      </div>
    </div>
  );
};

// Componente de Tabla de Probabilidad
const ProbabilityTable = ({ data, totalYears, darkMode, onTopicClick }) => {
  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-emerald-500" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-rose-500" />;
    return <Minus className="h-4 w-4 text-slate-400" />;
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 80) return darkMode ? 'text-emerald-400 bg-emerald-500/20' : 'text-emerald-700 bg-emerald-100';
    if (prob >= 50) return darkMode ? 'text-amber-400 bg-amber-500/20' : 'text-amber-700 bg-amber-100';
    if (prob >= 25) return darkMode ? 'text-blue-400 bg-blue-500/20' : 'text-blue-700 bg-blue-100';
    return darkMode ? 'text-slate-400 bg-slate-700/50' : 'text-slate-600 bg-slate-100';
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
      <table className="w-full">
        <thead>
          <tr className={darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}>
            <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Tema
            </th>
            <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Apariciones
            </th>
            <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Probabilidad
            </th>
            <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Tendencia
            </th>
            <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Predicción
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, idx) => (
            <motion.tr
              key={item.topic}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: idx * 0.05 }}
              onDoubleClick={() => onTopicClick && onTopicClick(item.topic)}
              className={`border-t cursor-pointer transition-colors ${
                darkMode ? 'border-slate-700/50 hover:bg-slate-800/30' : 'border-slate-200 hover:bg-slate-50'
              }`}
              title="Doble click para ver subtemas"
            >
              <td className={`px-4 py-3 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                <div className="flex items-center gap-2">
                  <Eye className={`h-4 w-4 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                  {item.probability >= 80 && <Flame className="h-4 w-4 text-orange-500" />}
                  <span className="font-medium">{item.topic}</span>
                </div>
              </td>
              <td className={`px-4 py-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                {item.yearsAppeared}/{totalYears}
              </td>
              <td className="px-4 py-3 text-center">
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-bold ${getProbabilityColor(item.probability)}`}>
                  {item.probability}%
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-center gap-1">
                  {getTrendIcon(item.trend)}
                  <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {item.trend === 'up' ? 'Subiendo' : item.trend === 'down' ? 'Bajando' : 'Estable'}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3 text-center">
                {item.probability >= 70 ? (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                    <Zap className="h-3 w-3" />
                    Muy probable
                  </span>
                ) : item.probability >= 40 ? (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${darkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                    Probable
                  </span>
                ) : (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${darkMode ? 'bg-slate-700/50 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                    Posible
                  </span>
                )}
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Componente Principal
const AreaTrendsDashboard = ({ questions = [], darkMode = false, onBack, onSelectArea, onGenerateExam }) => {
  // Estado para área seleccionada
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [viewMode, setViewMode] = useState('heatmap');
  const [barMode, setBarMode] = useState('byTopic'); // 'byTopic' | 'total'

  // Obtener lista de áreas únicas ordenadas por cantidad de preguntas
  const areas = useMemo(() => {
    const areaCounts = {};
    questions.forEach(q => {
      if (q.area) {
        areaCounts[q.area] = (areaCounts[q.area] || 0) + 1;
      }
    });
    return Object.entries(areaCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([area, count]) => ({ area, count }));
  }, [questions]);

  // Auto-seleccionar la primera área con más preguntas si no hay selección
  React.useEffect(() => {
    if (!selectedArea && areas.length > 0) {
      setSelectedArea(areas[0].area);
    }
  }, [areas, selectedArea]);

  // Resetear tema seleccionado cuando cambia el área
  React.useEffect(() => {
    setSelectedTopic(null);
  }, [selectedArea]);

  // Filtrar preguntas por área seleccionada
  const filteredQuestions = useMemo(() => {
    return questions.filter(q => q.area === selectedArea);
  }, [questions, selectedArea]);

  // Obtener años únicos ordenados
  const years = useMemo(() => {
    const yearSet = new Set();
    filteredQuestions.forEach(q => {
      if (q.year) yearSet.add(q.year);
    });
    return Array.from(yearSet).sort((a, b) => extractYear(a) - extractYear(b));
  }, [filteredQuestions]);

  // Obtener temas únicos
  const topics = useMemo(() => {
    const topicCounts = {};
    filteredQuestions.forEach(q => {
      if (q.topic) {
        topicCounts[q.topic] = (topicCounts[q.topic] || 0) + 1;
      }
    });
    return Object.entries(topicCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([topic, count]) => ({ topic, count }));
  }, [filteredQuestions]);

  // Datos para el heatmap
  const heatmapData = useMemo(() => {
    return topics.map(({ topic }) => {
      const row = { topic, total: 0 };
      years.forEach(year => {
        const count = filteredQuestions.filter(q => q.topic === topic && q.year === year).length;
        row[year] = count;
        row.total += count;
      });
      return row;
    });
  }, [topics, years, filteredQuestions]);

  // Datos para gráfico de barras apiladas
  const stackedBarData = useMemo(() => {
    return years.map(year => {
      const yearData = { year: year.replace('RM ', '') };
      const topTopics = topics.slice(0, 8);
      topTopics.forEach(({ topic }) => {
        yearData[topic] = filteredQuestions.filter(q => q.topic === topic && q.year === year).length;
      });
      if (topics.length > 8) {
        const otherTopics = topics.slice(8);
        yearData['Otros'] = filteredQuestions.filter(q =>
          otherTopics.some(t => t.topic === q.topic) && q.year === year
        ).length;
      }
      return yearData;
    });
  }, [years, topics, filteredQuestions]);

  // Datos para gráfico de líneas
  const lineChartData = useMemo(() => {
    return years.map(year => {
      const yearData = { year: year.replace('RM ', '') };
      topics.slice(0, 5).forEach(({ topic }) => {
        yearData[topic] = filteredQuestions.filter(q => q.topic === topic && q.year === year).length;
      });
      return yearData;
    });
  }, [years, topics, filteredQuestions]);

  // Datos para total de preguntas por año (Opción 1 - Toggle en barras)
  const totalByYearData = useMemo(() => {
    return years.map(year => {
      const total = filteredQuestions.filter(q => q.year === year).length;
      return {
        year: year.replace('RM ', ''),
        fullYear: year,
        total,
      };
    });
  }, [years, filteredQuestions]);

  // Análisis completo de tendencia (Opción 2 - Pestaña Tendencia)
  const trendAnalysis = useMemo(() => {
    if (years.length === 0) return null;

    // Datos básicos por año
    const yearlyData = years.map((year, index) => {
      const total = filteredQuestions.filter(q => q.year === year).length;
      const prevYear = index > 0 ? years[index - 1] : null;
      const prevTotal = prevYear ? filteredQuestions.filter(q => q.year === prevYear).length : null;

      let change = null;
      let changePercent = null;
      if (prevTotal !== null && prevTotal > 0) {
        change = total - prevTotal;
        changePercent = ((change / prevTotal) * 100);
      }

      return {
        year: year.replace('RM ', ''),
        fullYear: year,
        total,
        prevTotal,
        change,
        changePercent,
      };
    });

    // Estadísticas generales
    const totals = yearlyData.map(d => d.total);
    const sum = totals.reduce((a, b) => a + b, 0);
    const avg = sum / totals.length;
    const max = Math.max(...totals);
    const min = Math.min(...totals);
    const peakYear = yearlyData.find(d => d.total === max);
    const lowYear = yearlyData.find(d => d.total === min);

    // Tendencia general (regresión lineal simple)
    const n = yearlyData.length;
    const xSum = yearlyData.reduce((acc, _, i) => acc + i, 0);
    const ySum = sum;
    const xySum = yearlyData.reduce((acc, d, i) => acc + i * d.total, 0);
    const x2Sum = yearlyData.reduce((acc, _, i) => acc + i * i, 0);
    const slope = (n * xySum - xSum * ySum) / (n * x2Sum - xSum * xSum);

    let generalTrend = 'stable';
    if (slope > 0.5) generalTrend = 'up';
    else if (slope < -0.5) generalTrend = 'down';

    // Análisis de últimos 3 años vs anteriores
    const recentYears = yearlyData.slice(-3);
    const previousYears = yearlyData.slice(0, -3);
    const recentAvg = recentYears.length > 0
      ? recentYears.reduce((a, d) => a + d.total, 0) / recentYears.length
      : 0;
    const previousAvg = previousYears.length > 0
      ? previousYears.reduce((a, d) => a + d.total, 0) / previousYears.length
      : 0;

    let recentTrend = 'stable';
    if (previousAvg > 0) {
      const recentChange = ((recentAvg - previousAvg) / previousAvg) * 100;
      if (recentChange > 15) recentTrend = 'up';
      else if (recentChange < -15) recentTrend = 'down';
    }

    // Proyección simple para próximo año
    const lastValue = yearlyData[yearlyData.length - 1]?.total || 0;
    const projection = Math.round(lastValue + slope);
    const projectionMin = Math.round(projection * 0.85);
    const projectionMax = Math.round(projection * 1.15);

    // Variabilidad (desviación estándar)
    const variance = totals.reduce((acc, val) => acc + Math.pow(val - avg, 2), 0) / n;
    const stdDev = Math.sqrt(variance);
    const coefficientOfVariation = (stdDev / avg) * 100;

    let variability = 'baja';
    if (coefficientOfVariation > 30) variability = 'alta';
    else if (coefficientOfVariation > 15) variability = 'moderada';

    // Años consecutivos de crecimiento/decrecimiento
    let consecutiveGrowth = 0;
    let consecutiveDecline = 0;
    for (let i = yearlyData.length - 1; i > 0; i--) {
      if (yearlyData[i].change > 0) {
        if (consecutiveDecline === 0) consecutiveGrowth++;
        else break;
      } else if (yearlyData[i].change < 0) {
        if (consecutiveGrowth === 0) consecutiveDecline++;
        else break;
      } else {
        break;
      }
    }

    return {
      yearlyData,
      stats: {
        total: sum,
        average: Math.round(avg * 10) / 10,
        max,
        min,
        peakYear,
        lowYear,
        stdDev: Math.round(stdDev * 10) / 10,
        variability,
        coefficientOfVariation: Math.round(coefficientOfVariation),
      },
      trend: {
        general: generalTrend,
        slope: Math.round(slope * 100) / 100,
        recent: recentTrend,
        recentAvg: Math.round(recentAvg * 10) / 10,
        previousAvg: Math.round(previousAvg * 10) / 10,
        consecutiveGrowth,
        consecutiveDecline,
      },
      projection: {
        value: projection > 0 ? projection : 0,
        min: projectionMin > 0 ? projectionMin : 0,
        max: projectionMax,
      }
    };
  }, [years, filteredQuestions]);

  // Datos para Bump Chart (ranking de temas por año)
  const bumpChartData = useMemo(() => {
    if (years.length === 0 || topics.length === 0) return { chartData: [], topicsInfo: [] };

    const TOP_N = 8; // Mostrar top 8 temas
    const topTopics = topics.slice(0, TOP_N);

    // Calcular ranking por año
    const rankingsByYear = {};
    years.forEach(year => {
      const yearCounts = topTopics.map(({ topic }) => ({
        topic,
        count: filteredQuestions.filter(q => q.topic === topic && q.year === year).length
      }));

      // Ordenar por count descendente y asignar ranking
      yearCounts.sort((a, b) => b.count - a.count);
      rankingsByYear[year] = {};
      yearCounts.forEach((item, index) => {
        rankingsByYear[year][item.topic] = {
          rank: index + 1,
          count: item.count
        };
      });
    });

    // Formato para el gráfico: array de puntos por año
    const chartData = years.map(year => {
      const dataPoint = { year: year.replace('RM ', ''), fullYear: year };
      topTopics.forEach(({ topic }) => {
        const info = rankingsByYear[year][topic];
        dataPoint[topic] = info?.rank || TOP_N + 1;
        dataPoint[`${topic}_count`] = info?.count || 0;
      });
      return dataPoint;
    });

    // Información adicional de cada tema (color, tendencia de ranking)
    const topicsInfo = topTopics.map(({ topic, count }, index) => {
      const firstYearRank = rankingsByYear[years[0]]?.[topic]?.rank || TOP_N;
      const lastYearRank = rankingsByYear[years[years.length - 1]]?.[topic]?.rank || TOP_N;
      const rankChange = firstYearRank - lastYearRank; // Positivo = mejoró, negativo = empeoró

      return {
        topic,
        totalCount: count,
        color: TOPIC_COLORS[index % TOPIC_COLORS.length],
        firstRank: firstYearRank,
        lastRank: lastYearRank,
        rankChange,
        trend: rankChange > 0 ? 'up' : rankChange < 0 ? 'down' : 'stable'
      };
    });

    // Ordenar topicsInfo por ranking actual (último año)
    topicsInfo.sort((a, b) => a.lastRank - b.lastRank);

    return { chartData, topicsInfo, topTopics };
  }, [years, topics, filteredQuestions]);

  // Datos para tabla de probabilidad
  const probabilityData = useMemo(() => {
    return topics.map(({ topic, count }) => {
      const yearsWithTopic = new Set();
      filteredQuestions.forEach(q => {
        if (q.topic === topic && q.year) {
          yearsWithTopic.add(q.year);
        }
      });

      const yearsAppeared = yearsWithTopic.size;
      const probability = Math.round((yearsAppeared / years.length) * 100);

      const recentYears = years.slice(-3);
      const previousYears = years.slice(-6, -3);

      const recentCount = filteredQuestions.filter(q =>
        q.topic === topic && recentYears.includes(q.year)
      ).length;

      const previousCount = filteredQuestions.filter(q =>
        q.topic === topic && previousYears.includes(q.year)
      ).length;

      let trend = 'stable';
      if (previousCount > 0) {
        const change = ((recentCount - previousCount) / previousCount) * 100;
        if (change > 20) trend = 'up';
        else if (change < -20) trend = 'down';
      } else if (recentCount > 0) {
        trend = 'up';
      }

      return { topic, total: count, yearsAppeared, probability, trend };
    });
  }, [topics, years, filteredQuestions]);

  // Estadísticas generales
  const stats = useMemo(() => {
    const totalQuestions = filteredQuestions.length;
    const totalTopics = topics.length;
    const topTopic = topics[0] || { topic: 'N/A', count: 0 };

    const lastYear = years[years.length - 1];
    const prevYear = years[years.length - 2];
    const lastYearCount = filteredQuestions.filter(q => q.year === lastYear).length;
    const prevYearCount = filteredQuestions.filter(q => q.year === prevYear).length;
    const yearTrend = prevYearCount > 0 ? ((lastYearCount - prevYearCount) / prevYearCount) * 100 : 0;

    return {
      totalQuestions,
      totalTopics,
      totalYears: years.length,
      topTopic,
      yearTrend,
      avgPerYear: Math.round(totalQuestions / years.length * 10) / 10
    };
  }, [filteredQuestions, topics, years]);

  // Datos para pie chart
  const pieData = useMemo(() => {
    return topics.slice(0, 6).map(({ topic, count }, index) => ({
      name: topic,
      value: count,
      fill: TOPIC_COLORS[index % TOPIC_COLORS.length]
    }));
  }, [topics]);

  // Handler para cambio de área
  const handleAreaChange = useCallback((area) => {
    setSelectedArea(area);
    setSelectedTopic(null);
    if (onSelectArea) onSelectArea(area);
  }, [onSelectArea]);

  // Handler para click en tema
  const handleTopicClick = useCallback((topic) => {
    setSelectedTopic(prev => prev === topic ? null : topic);
  }, []);

  // Handler para generar examen desde el panel de subtemas
  const handleGenerateExamFromDrilldown = useCallback((topic, subtopics) => {
    if (onGenerateExam) {
      onGenerateExam({
        area: selectedArea,
        topic,
        subtopics,
        questions: filteredQuestions.filter(q => {
          if (q.topic !== topic) return false;
          if (subtopics && subtopics.length > 0) {
            return subtopics.includes(q.subtopic || 'Sin especificar');
          }
          return true;
        })
      });
    }
  }, [selectedArea, filteredQuestions, onGenerateExam]);

  // Renderizar vista según modo seleccionado
  const renderVisualization = () => {
    switch (viewMode) {
      case 'bars':
        return (
          <div>
            {/* Toggle Por Temas / Total */}
            <div className="flex items-center justify-end mb-4">
              <div className={`inline-flex rounded-lg p-1 ${darkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                <button
                  onClick={() => setBarMode('byTopic')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    barMode === 'byTopic'
                      ? 'bg-blue-500 text-white shadow'
                      : darkMode ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Por Temas
                </button>
                <button
                  onClick={() => setBarMode('total')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    barMode === 'total'
                      ? 'bg-blue-500 text-white shadow'
                      : darkMode ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Total por Año
                </button>
              </div>
            </div>

            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                {barMode === 'total' ? (
                  <BarChart data={totalByYearData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                    <XAxis dataKey="year" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                    <YAxis tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                        border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        color: darkMode ? '#f3f4f6' : '#1f2937'
                      }}
                      formatter={(value) => [`${value} preguntas`, 'Total']}
                      labelFormatter={(label) => `Año: ${label}`}
                    />
                    <Bar dataKey="total" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                      {totalByYearData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={index === totalByYearData.length - 1 ? '#10B981' : '#3B82F6'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                ) : (
                  <BarChart data={stackedBarData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                    <XAxis dataKey="year" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                    <YAxis tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                        border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        color: darkMode ? '#f3f4f6' : '#1f2937'
                      }}
                    />
                    <Legend />
                    {topics.slice(0, 8).map(({ topic }, index) => (
                      <Bar key={topic} dataKey={topic} stackId="a" fill={TOPIC_COLORS[index % TOPIC_COLORS.length]} />
                    ))}
                    {topics.length > 8 && <Bar dataKey="Otros" stackId="a" fill="#6b7280" />}
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>

            {/* Leyenda adicional para modo Total */}
            {barMode === 'total' && (
              <div className={`mt-4 flex items-center justify-center gap-6 text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-blue-500" />
                  <span>Años anteriores</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-emerald-500" />
                  <span>Último año</span>
                </div>
              </div>
            )}
          </div>
        );

      case 'lines':
        return (
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={lineChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                <XAxis dataKey="year" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                <YAxis tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                    border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    color: darkMode ? '#f3f4f6' : '#1f2937'
                  }}
                />
                <Legend />
                {topics.slice(0, 5).map(({ topic }, index) => (
                  <Line
                    key={topic}
                    type="monotone"
                    dataKey={topic}
                    stroke={TOPIC_COLORS[index % TOPIC_COLORS.length]}
                    strokeWidth={2}
                    dot={{ fill: TOPIC_COLORS[index % TOPIC_COLORS.length], strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        );

      case 'pie':
        return (
          <div className="h-[400px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={140}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                    border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    color: darkMode ? '#f3f4f6' : '#1f2937'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        );

      case 'trend':
        if (!trendAnalysis) return <div>No hay datos suficientes</div>;
        return (
          <div className="space-y-6">
            {/* Gráfico principal de tendencia */}
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trendAnalysis.yearlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                  <XAxis dataKey="year" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                  <YAxis tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                      borderRadius: '12px',
                      color: darkMode ? '#f3f4f6' : '#1f2937',
                      padding: '12px'
                    }}
                    formatter={(value, name) => {
                      if (name === 'total') return [`${value} preguntas`, 'Total'];
                      return [value, name];
                    }}
                    labelFormatter={(label) => `Año: ${label}`}
                  />
                  <ReferenceLine
                    y={trendAnalysis.stats.average}
                    stroke={darkMode ? '#f59e0b' : '#d97706'}
                    strokeDasharray="5 5"
                    label={{
                      value: `Promedio: ${trendAnalysis.stats.average}`,
                      fill: darkMode ? '#f59e0b' : '#d97706',
                      fontSize: 11,
                      position: 'right'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    fill="url(#colorTotal)"
                  />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="#3B82F6"
                    strokeWidth={3}
                    dot={{ fill: '#3B82F6', strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 8, fill: '#2563eb' }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Cards de estadísticas */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Promedio */}
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-blue-50'} border ${darkMode ? 'border-slate-600' : 'border-blue-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Hash className={`h-4 w-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-blue-600'}`}>Promedio/Año</span>
                </div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-blue-900'}`}>
                  {trendAnalysis.stats.average}
                </p>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-blue-600'}`}>
                  preguntas
                </p>
              </div>

              {/* Año Pico */}
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-emerald-50'} border ${darkMode ? 'border-slate-600' : 'border-emerald-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpRight className={`h-4 w-4 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-emerald-600'}`}>Año Pico</span>
                </div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-emerald-900'}`}>
                  {trendAnalysis.stats.peakYear?.year}
                </p>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-emerald-600'}`}>
                  {trendAnalysis.stats.max} preguntas
                </p>
              </div>

              {/* Año Mínimo */}
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-amber-50'} border ${darkMode ? 'border-slate-600' : 'border-amber-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownRight className={`h-4 w-4 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                  <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-amber-600'}`}>Año Mínimo</span>
                </div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-amber-900'}`}>
                  {trendAnalysis.stats.lowYear?.year}
                </p>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-amber-600'}`}>
                  {trendAnalysis.stats.min} preguntas
                </p>
              </div>

              {/* Variabilidad */}
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-purple-50'} border ${darkMode ? 'border-slate-600' : 'border-purple-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Activity className={`h-4 w-4 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                  <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-purple-600'}`}>Variabilidad</span>
                </div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-purple-900'}`}>
                  {trendAnalysis.stats.variability}
                </p>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-purple-600'}`}>
                  CV: {trendAnalysis.stats.coefficientOfVariation}%
                </p>
              </div>
            </div>

            {/* Análisis de Tendencia y Proyección */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Panel de Tendencia */}
              <div className={`p-5 rounded-xl ${darkMode ? 'bg-slate-700/30' : 'bg-gray-50'} border ${darkMode ? 'border-slate-600' : 'border-gray-200'}`}>
                <h4 className={`flex items-center gap-2 font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  <TrendingUp className="h-5 w-5" />
                  Análisis de Tendencia
                </h4>
                <div className="space-y-4">
                  {/* Tendencia General */}
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>Tendencia General:</span>
                    <span className={`flex items-center gap-1 font-semibold ${
                      trendAnalysis.trend.general === 'up' ? 'text-emerald-500' :
                      trendAnalysis.trend.general === 'down' ? 'text-rose-500' :
                      darkMode ? 'text-slate-400' : 'text-gray-500'
                    }`}>
                      {trendAnalysis.trend.general === 'up' && <TrendingUp className="h-4 w-4" />}
                      {trendAnalysis.trend.general === 'down' && <TrendingDown className="h-4 w-4" />}
                      {trendAnalysis.trend.general === 'stable' && <Minus className="h-4 w-4" />}
                      {trendAnalysis.trend.general === 'up' ? 'Creciente' :
                       trendAnalysis.trend.general === 'down' ? 'Decreciente' : 'Estable'}
                    </span>
                  </div>

                  {/* Tendencia Reciente */}
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>Últimos 3 años:</span>
                    <span className={`flex items-center gap-1 font-semibold ${
                      trendAnalysis.trend.recent === 'up' ? 'text-emerald-500' :
                      trendAnalysis.trend.recent === 'down' ? 'text-rose-500' :
                      darkMode ? 'text-slate-400' : 'text-gray-500'
                    }`}>
                      {trendAnalysis.trend.recent === 'up' && <TrendingUp className="h-4 w-4" />}
                      {trendAnalysis.trend.recent === 'down' && <TrendingDown className="h-4 w-4" />}
                      {trendAnalysis.trend.recent === 'stable' && <Minus className="h-4 w-4" />}
                      Prom: {trendAnalysis.trend.recentAvg}
                    </span>
                  </div>

                  {/* Comparación con histórico */}
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>Promedio histórico:</span>
                    <span className={`font-semibold ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                      {trendAnalysis.trend.previousAvg}
                    </span>
                  </div>

                  {/* Racha */}
                  {(trendAnalysis.trend.consecutiveGrowth > 1 || trendAnalysis.trend.consecutiveDecline > 1) && (
                    <div className={`mt-3 p-3 rounded-lg ${
                      trendAnalysis.trend.consecutiveGrowth > 1
                        ? darkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'
                        : darkMode ? 'bg-rose-500/20' : 'bg-rose-100'
                    }`}>
                      <span className={`text-sm font-medium ${
                        trendAnalysis.trend.consecutiveGrowth > 1
                          ? darkMode ? 'text-emerald-400' : 'text-emerald-700'
                          : darkMode ? 'text-rose-400' : 'text-rose-700'
                      }`}>
                        {trendAnalysis.trend.consecutiveGrowth > 1
                          ? `${trendAnalysis.trend.consecutiveGrowth} años consecutivos de crecimiento`
                          : `${trendAnalysis.trend.consecutiveDecline} años consecutivos de descenso`
                        }
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Panel de Proyección */}
              <div className={`p-5 rounded-xl ${
                darkMode
                  ? 'bg-gradient-to-br from-blue-900/30 to-purple-900/30 border-blue-500/30'
                  : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'
              } border`}>
                <h4 className={`flex items-center gap-2 font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  <Zap className="h-5 w-5 text-amber-500" />
                  Proyección Próximo Año
                </h4>
                <div className="text-center py-4">
                  <p className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    ~{trendAnalysis.projection.value}
                  </p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    preguntas estimadas
                  </p>
                  <p className={`text-xs mt-2 ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                    Rango: {trendAnalysis.projection.min} - {trendAnalysis.projection.max}
                  </p>
                </div>
                <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-white/80'}`}>
                  <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    <Info className="h-3 w-3 inline mr-1" />
                    Proyección basada en tendencia histórica. La cantidad real puede variar.
                  </p>
                </div>
              </div>
            </div>

            {/* Tabla de cambios año a año */}
            <div className={`rounded-xl border ${darkMode ? 'border-slate-700' : 'border-gray-200'} overflow-hidden`}>
              <div className={`px-4 py-3 ${darkMode ? 'bg-slate-800' : 'bg-gray-50'} border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Detalle por Año
                </h4>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                <table className="w-full">
                  <thead className={`sticky top-0 ${darkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
                    <tr>
                      <th className={`px-4 py-2 text-left text-xs font-semibold ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>Año</th>
                      <th className={`px-4 py-2 text-center text-xs font-semibold ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>Total</th>
                      <th className={`px-4 py-2 text-center text-xs font-semibold ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>Cambio</th>
                      <th className={`px-4 py-2 text-center text-xs font-semibold ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>% Cambio</th>
                      <th className={`px-4 py-2 text-center text-xs font-semibold ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>vs Promedio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trendAnalysis.yearlyData.map((item, idx) => {
                      const vsAvg = item.total - trendAnalysis.stats.average;
                      return (
                        <tr
                          key={item.year}
                          className={`border-t ${darkMode ? 'border-slate-700/50' : 'border-gray-100'} ${
                            idx === trendAnalysis.yearlyData.length - 1 ? darkMode ? 'bg-blue-900/20' : 'bg-blue-50' : ''
                          }`}
                        >
                          <td className={`px-4 py-2.5 font-medium ${darkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                            {item.fullYear}
                            {idx === trendAnalysis.yearlyData.length - 1 && (
                              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-blue-500/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                                Último
                              </span>
                            )}
                          </td>
                          <td className={`px-4 py-2.5 text-center font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {item.total}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            {item.change !== null ? (
                              <span className={`inline-flex items-center gap-1 ${
                                item.change > 0 ? 'text-emerald-500' :
                                item.change < 0 ? 'text-rose-500' :
                                darkMode ? 'text-slate-400' : 'text-gray-500'
                              }`}>
                                {item.change > 0 && <ArrowUpRight className="h-3 w-3" />}
                                {item.change < 0 && <ArrowDownRight className="h-3 w-3" />}
                                {item.change > 0 ? '+' : ''}{item.change}
                              </span>
                            ) : (
                              <span className={darkMode ? 'text-slate-500' : 'text-gray-400'}>-</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            {item.changePercent !== null ? (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                item.changePercent > 10 ? darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700' :
                                item.changePercent < -10 ? darkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-100 text-rose-700' :
                                darkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {item.changePercent > 0 ? '+' : ''}{item.changePercent.toFixed(1)}%
                              </span>
                            ) : (
                              <span className={darkMode ? 'text-slate-500' : 'text-gray-400'}>-</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`text-sm ${
                              vsAvg > 2 ? 'text-emerald-500' :
                              vsAvg < -2 ? 'text-rose-500' :
                              darkMode ? 'text-slate-400' : 'text-gray-500'
                            }`}>
                              {vsAvg > 0 ? '+' : ''}{vsAvg.toFixed(1)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* OPCIÓN B: Sección de Evolución del Ranking dentro de Tendencia */}
            {bumpChartData.chartData.length > 0 && (
              <div className={`rounded-xl border ${darkMode ? 'border-slate-700' : 'border-gray-200'} overflow-hidden`}>
                <div className={`px-4 py-3 ${darkMode ? 'bg-slate-800' : 'bg-gray-50'} border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-2">
                    <Trophy className={`h-5 w-5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                    <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      Evolución del Ranking de Temas
                    </h4>
                  </div>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    ¿Qué temas ganaron o perdieron relevancia con el tiempo?
                  </p>
                </div>

                <div className="p-4">
                  {/* Mini Bump Chart */}
                  <div className="h-[280px] mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={bumpChartData.chartData} margin={{ top: 10, right: 100, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                        <XAxis
                          dataKey="year"
                          tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 11 }}
                        />
                        <YAxis
                          reversed
                          domain={[1, 8]}
                          ticks={[1, 2, 3, 4, 5, 6, 7, 8]}
                          tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 11 }}
                          width={30}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                            border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                            borderRadius: '8px',
                            fontSize: '12px'
                          }}
                          formatter={(value, name) => [`#${value}`, name]}
                        />
                        {bumpChartData.topicsInfo.slice(0, 6).map((info) => (
                          <Line
                            key={info.topic}
                            type="monotone"
                            dataKey={info.topic}
                            stroke={info.color}
                            strokeWidth={2}
                            dot={{ fill: info.color, r: 3 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Resumen rápido de cambios */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-emerald-900/20' : 'bg-emerald-50'}`}>
                      <div className="flex items-center gap-1 mb-1">
                        <TrendingUp className={`h-4 w-4 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                        <span className={`text-xs font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Subieron</span>
                      </div>
                      <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-emerald-900'}`}>
                        {bumpChartData.topicsInfo.filter(t => t.rankChange > 0).length}
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700/30' : 'bg-gray-100'}`}>
                      <div className="flex items-center gap-1 mb-1">
                        <Minus className={`h-4 w-4 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`} />
                        <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>Estables</span>
                      </div>
                      <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {bumpChartData.topicsInfo.filter(t => t.rankChange === 0).length}
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-rose-900/20' : 'bg-rose-50'}`}>
                      <div className="flex items-center gap-1 mb-1">
                        <TrendingDown className={`h-4 w-4 ${darkMode ? 'text-rose-400' : 'text-rose-600'}`} />
                        <span className={`text-xs font-medium ${darkMode ? 'text-rose-400' : 'text-rose-700'}`}>Bajaron</span>
                      </div>
                      <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-rose-900'}`}>
                        {bumpChartData.topicsInfo.filter(t => t.rankChange < 0).length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'ranking':
        if (!bumpChartData.chartData.length) return <div>No hay datos suficientes</div>;
        return (
          <div className="space-y-6">
            {/* Título y descripción */}
            <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/30' : 'bg-blue-50'} border ${darkMode ? 'border-slate-600' : 'border-blue-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Trophy className={`h-5 w-5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Evolución del Ranking de Temas
                </h4>
              </div>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Visualiza cómo ha cambiado la posición de cada tema a lo largo de los años.
                Una línea que sube indica que el tema ganó relevancia (mejor ranking).
              </p>
            </div>

            {/* Bump Chart */}
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={bumpChartData.chartData} margin={{ top: 20, right: 120, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                  <XAxis
                    dataKey="year"
                    tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                  />
                  <YAxis
                    reversed
                    domain={[1, 8]}
                    ticks={[1, 2, 3, 4, 5, 6, 7, 8]}
                    tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                    label={{
                      value: 'Ranking',
                      angle: -90,
                      position: 'insideLeft',
                      fill: darkMode ? '#9ca3af' : '#6b7280',
                      fontSize: 12
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                      borderRadius: '12px',
                      color: darkMode ? '#f3f4f6' : '#1f2937',
                      padding: '12px'
                    }}
                    formatter={(value, name, props) => {
                      const countKey = `${name}_count`;
                      const count = props.payload?.[countKey] || 0;
                      return [`#${value} (${count} preguntas)`, name];
                    }}
                    labelFormatter={(label) => `Año: ${label}`}
                  />
                  {bumpChartData.topicsInfo.map((info, index) => (
                    <Line
                      key={info.topic}
                      type="monotone"
                      dataKey={info.topic}
                      stroke={info.color}
                      strokeWidth={3}
                      dot={{ fill: info.color, strokeWidth: 2, r: 5 }}
                      activeDot={{ r: 8 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Leyenda con información de ranking */}
            <div className={`rounded-xl border ${darkMode ? 'border-slate-700' : 'border-gray-200'} overflow-hidden`}>
              <div className={`px-4 py-3 ${darkMode ? 'bg-slate-800' : 'bg-gray-50'} border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Resumen de Cambios en Ranking
                </h4>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {bumpChartData.topicsInfo.map((info) => (
                    <div
                      key={info.topic}
                      className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700/30' : 'bg-gray-50'} border-l-4`}
                      style={{ borderLeftColor: info.color }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium truncate ${darkMode ? 'text-slate-200' : 'text-gray-700'}`} title={info.topic}>
                          {info.topic}
                        </span>
                        <span className={`flex items-center gap-1 text-xs font-semibold ${
                          info.trend === 'up' ? 'text-emerald-500' :
                          info.trend === 'down' ? 'text-rose-500' :
                          darkMode ? 'text-slate-400' : 'text-gray-500'
                        }`}>
                          {info.trend === 'up' && <TrendingUp className="h-3 w-3" />}
                          {info.trend === 'down' && <TrendingDown className="h-3 w-3" />}
                          {info.trend === 'stable' && <Minus className="h-3 w-3" />}
                          {info.rankChange > 0 ? `+${info.rankChange}` : info.rankChange}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className={darkMode ? 'text-slate-400' : 'text-gray-500'}>
                          #{info.firstRank}
                        </span>
                        <span className={darkMode ? 'text-slate-500' : 'text-gray-400'}>→</span>
                        <span className={`font-semibold ${
                          info.lastRank <= 3 ? 'text-amber-500' : darkMode ? 'text-slate-300' : 'text-gray-700'
                        }`}>
                          #{info.lastRank}
                        </span>
                        <span className={`ml-auto ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                          {info.totalCount} total
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Insights del ranking */}
            <div className="grid md:grid-cols-3 gap-4">
              {/* Ganadores */}
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-emerald-900/20' : 'bg-emerald-50'} border ${darkMode ? 'border-emerald-500/30' : 'border-emerald-200'}`}>
                <h5 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                  <TrendingUp className="h-4 w-4" />
                  Ganaron Posiciones
                </h5>
                <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                  {bumpChartData.topicsInfo.filter(t => t.rankChange > 0).slice(0, 3).map(t => (
                    <li key={t.topic} className="flex items-center justify-between">
                      <span className="truncate mr-2">{t.topic}</span>
                      <span className="text-emerald-500 font-semibold">+{t.rankChange}</span>
                    </li>
                  ))}
                  {bumpChartData.topicsInfo.filter(t => t.rankChange > 0).length === 0 && (
                    <li className="italic text-slate-400">Ninguno</li>
                  )}
                </ul>
              </div>

              {/* Perdedores */}
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-rose-900/20' : 'bg-rose-50'} border ${darkMode ? 'border-rose-500/30' : 'border-rose-200'}`}>
                <h5 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-rose-400' : 'text-rose-700'}`}>
                  <TrendingDown className="h-4 w-4" />
                  Perdieron Posiciones
                </h5>
                <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                  {bumpChartData.topicsInfo.filter(t => t.rankChange < 0).slice(0, 3).map(t => (
                    <li key={t.topic} className="flex items-center justify-between">
                      <span className="truncate mr-2">{t.topic}</span>
                      <span className="text-rose-500 font-semibold">{t.rankChange}</span>
                    </li>
                  ))}
                  {bumpChartData.topicsInfo.filter(t => t.rankChange < 0).length === 0 && (
                    <li className="italic text-slate-400">Ninguno</li>
                  )}
                </ul>
              </div>

              {/* Top 3 actual */}
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-amber-900/20' : 'bg-amber-50'} border ${darkMode ? 'border-amber-500/30' : 'border-amber-200'}`}>
                <h5 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                  <Medal className="h-4 w-4" />
                  Top 3 Actual
                </h5>
                <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                  {bumpChartData.topicsInfo.filter(t => t.lastRank <= 3).sort((a, b) => a.lastRank - b.lastRank).map((t, idx) => (
                    <li key={t.topic} className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-amber-400 text-amber-900' :
                        idx === 1 ? 'bg-slate-300 text-slate-700' :
                        'bg-amber-600 text-white'
                      }`}>
                        {idx + 1}
                      </span>
                      <span className="truncate">{t.topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );

      case 'heatmap':
      default:
        return (
          <TopicHeatmap
            data={heatmapData}
            years={years}
            darkMode={darkMode}
            onTopicClick={handleTopicClick}
          />
        );
    }
  };

  // Si hay un tema seleccionado, mostrar vista dedicada de subtemas
  if (selectedTopic) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Header con navegación */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => setSelectedTopic(null)}
              className={`p-2 rounded-xl transition-colors ${
                darkMode
                  ? 'hover:bg-slate-800 text-slate-400 hover:text-slate-200'
                  : 'hover:bg-gray-200 text-gray-600 hover:text-gray-800'
              }`}
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                {selectedArea} → Análisis de Subtemas
              </p>
              <h1 className={`text-2xl md:text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {selectedTopic}
              </h1>
            </div>
          </div>

          {/* Panel completo de análisis de subtemas */}
          <SubtopicDrilldownPanel
            topic={selectedTopic}
            questions={filteredQuestions}
            years={years}
            darkMode={darkMode}
            onClose={() => setSelectedTopic(null)}
            onGenerateExam={handleGenerateExamFromDrilldown}
            isFullView={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className={`p-2 rounded-xl transition-colors ${
                  darkMode
                    ? 'hover:bg-slate-800 text-slate-400 hover:text-slate-200'
                    : 'hover:bg-gray-200 text-gray-600 hover:text-gray-800'
                }`}
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <h1 className={`text-2xl md:text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Análisis de Tendencias
              </h1>
              <p className={`mt-1 text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Visualiza patrones, predice temas y enfoca tu estudio
              </p>
            </div>
          </div>

          {/* Selector de Área */}
          <div className="flex items-center gap-3">
            <Filter className={`h-5 w-5 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`} />
            <select
              value={selectedArea}
              onChange={(e) => handleAreaChange(e.target.value)}
              className={`px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${
                darkMode
                  ? 'bg-slate-800 border-slate-700 text-white focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-700 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
            >
              {areas.map(({ area, count }) => (
                <option key={area} value={area}>
                  {area} ({count})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Preguntas"
            value={stats.totalQuestions}
            subtitle={`${stats.avgPerYear} promedio/año`}
            icon={Activity}
            darkMode={darkMode}
            accentColor="blue"
          />
          <StatCard
            title="Años Analizados"
            value={stats.totalYears}
            subtitle={`${years[0]?.replace('RM ', '')} - ${years[years.length - 1]?.replace('RM ', '')}`}
            icon={Calendar}
            darkMode={darkMode}
            accentColor="green"
          />
          <StatCard
            title="Tema Líder"
            value={stats.topTopic.topic}
            subtitle={`${stats.topTopic.count} preguntas (${Math.round(stats.topTopic.count / stats.totalQuestions * 100)}%)`}
            icon={Award}
            darkMode={darkMode}
            accentColor="amber"
            clickable={true}
            onClick={() => handleTopicClick(stats.topTopic.topic)}
          />
          <StatCard
            title="Tendencia Anual"
            value={`${stats.yearTrend >= 0 ? '+' : ''}${stats.yearTrend.toFixed(1)}%`}
            subtitle="vs año anterior"
            icon={TrendingUp}
            trend={stats.yearTrend}
            darkMode={darkMode}
            accentColor="purple"
          />
        </div>

        {/* Visualización Principal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 mb-8 shadow-lg`}
        >
          {/* Tabs de visualización */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Distribución de Temas por Año
              </h2>
              <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Haz clic en un tema para ver el análisis de subtemas
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { id: 'heatmap', label: 'Heatmap', icon: BarChart3 },
                { id: 'bars', label: 'Barras', icon: BarChart2 },
                { id: 'trend', label: 'Tendencia', icon: TrendingUp },
                { id: 'ranking', label: 'Ranking', icon: Trophy },
                { id: 'lines', label: 'Líneas', icon: Activity },
                { id: 'pie', label: 'Circular', icon: PieChartIcon },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => {
                    setViewMode(id);
                    if (id !== 'heatmap') setSelectedTopic(null);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewMode === id
                      ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                      : darkMode
                        ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {renderVisualization()}
        </motion.div>

        {/* Tabla de Probabilidad */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 shadow-lg`}
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Probabilidad de Aparición por Tema
              </h2>
              <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Haz clic en un tema para ver el desglose de subtemas
              </p>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
              darkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'
            }`}>
              <Info className="h-3.5 w-3.5" />
              <span>{stats.totalYears} años de data</span>
            </div>
          </div>

          <ProbabilityTable
            data={probabilityData}
            totalYears={stats.totalYears}
            darkMode={darkMode}
            onTopicClick={handleTopicClick}
          />
        </motion.div>

        {/* Insights Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={`mt-8 rounded-2xl border ${darkMode ? 'bg-gradient-to-br from-blue-900/30 to-purple-900/30 border-blue-500/30' : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'} p-6`}
        >
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            💡 Insights para {selectedArea}
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Temas Imperdibles */}
            <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
              <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                <Flame className="h-4 w-4" />
                Temas Imperdibles (80%+ probabilidad)
              </h4>
              <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                {probabilityData.filter(t => t.probability >= 80).slice(0, 5).map(t => (
                  <li
                    key={t.topic}
                    className="flex items-center gap-2 cursor-pointer hover:text-teal-500 transition-colors"
                    onDoubleClick={() => handleTopicClick(t.topic)}
                    title="Doble click para ver subtemas"
                  >
                    <span className="text-emerald-500">✓</span>
                    <span>{t.topic}</span>
                    <span className={`ml-auto font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      {t.probability}%
                    </span>
                  </li>
                ))}
                {probabilityData.filter(t => t.probability >= 80).length === 0 && (
                  <li className="text-slate-400 italic">No hay temas con probabilidad ≥80%</li>
                )}
              </ul>
            </div>

            {/* Temas en Tendencia */}
            <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
              <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                <TrendingUp className="h-4 w-4" />
                Temas en Tendencia Alcista
              </h4>
              <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                {probabilityData.filter(t => t.trend === 'up').slice(0, 5).map(t => (
                  <li
                    key={t.topic}
                    className="flex items-center gap-2 cursor-pointer hover:text-teal-500 transition-colors"
                    onDoubleClick={() => handleTopicClick(t.topic)}
                    title="Doble click para ver subtemas"
                  >
                    <TrendingUp className="h-3 w-3 text-emerald-500" />
                    <span>{t.topic}</span>
                    <span className={`ml-auto text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {t.total} preguntas
                    </span>
                  </li>
                ))}
                {probabilityData.filter(t => t.trend === 'up').length === 0 && (
                  <li className="text-slate-400 italic">No hay temas en tendencia alcista</li>
                )}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AreaTrendsDashboard;
